import isTestFieldRequired from '@app.raytd.com/common/utils/test-suites/getFieldRequirement';
import { compare } from 'natural-orderby';
import { TestSuite } from 'store/src/lib/tests/entity';
import {
  convertRatingToScore,
  convertScoreToRating,
  getOverallRatingLabel,
} from '../ratingHelpers';
import convertTestToReportItem from './convertTestItem';
import { ReportItem, ReportItemContent, ReportTestItem } from './entities';
import { customCompare } from './sorter';
import { countTreeItems } from './utils';
import { hasSubcategoryTier } from '@/app/features/testsuites/specifications/subcategory-tier';

export function scoreCompare(
  a: string | undefined,
  b: string | undefined
): number {
  // Handle undefined cases first: undefined should be last
  if (a === undefined && b === undefined) return 0; // Both undefined, equal
  if (a === undefined) return 1; // a is undefined, comes after b
  if (b === undefined) return -1; // b is undefined, comes before a

  // Now handle the original string comparison logic for defined strings
  const missingPattern = /^\(Missing .*/;
  const noPattern = /^\(No .*/;

  const isAMissingItem = missingPattern.test(a);
  const isBMissingItem = missingPattern.test(b);
  const isANoItem = noPattern.test(a);
  const isBNoItem = noPattern.test(b);

  // Sort "Missing" items after regular items
  if (isAMissingItem && !isBMissingItem) return 1;
  if (!isAMissingItem && isBMissingItem) return -1;

  // Sort "No" items after regular items (but before Missing items)
  if (isANoItem && !isBNoItem && !isBMissingItem) return 1;
  if (!isANoItem && isBNoItem && !isAMissingItem) return -1;

  // Otherwise, apply natural sort in descending order for defined strings
  return compare({
    order: 'desc',
  })(a, b);
}

export default function convertScoreToTree(
  testItems: ReportTestItem[],
  testSuites: TestSuite[]
): ReportItem[] {
  // First level map for scores
  const scoreMap: { [key: string]: ReportItem } = {};

  // Helper to get normalized suite title
  const getSuiteTitle = (item: ReportTestItem): string => {
    return item.testSpecification?.test_suite_title?.trim() || '(No Suite)';
  };

  const getScoreKey = (
    scoreValue: number | undefined,
    item: ReportTestItem,
    testSuites: TestSuite[]
  ): string => {
    if (scoreValue !== undefined && scoreValue !== null) {
      return scoreValue.toString();
    }

    if (item.ratingType === 'generic') {
      return 'no_rating';
    }

    const testSuite = testSuites.find(
      (ts) => ts.id === item.testSpecification?.testSuiteId
    );

    const isRatingRequired = testSuite
      ? isTestFieldRequired(testSuite, 'rating')
      : false;

    if (!item.result && !isRatingRequired && item.testSpecification?.type === 'compliance') {
      return 'na_rating';
    }

    if (item.result) {
      return 'has_rating';
    }

    return 'missing_rating';
  };

  // Dataset-level mix: if the filtered dataset has both types, show mixed labels for all buckets
  const hasComplianceTests = testItems.some(
    (item) => item.testSpecification?.type === 'compliance'
  );
  const hasConditionalTests = testItems.some(
    (item) => item.testSpecification?.type === 'condition'
  );
  const isMixedDataset = hasComplianceTests && hasConditionalTests;

  const getRatingLabel = (item: ReportTestItem): string => {
    const testSuite = testSuites.find(
      (ts) => ts.id === item.testSpecification?.testSuiteId
    );
    const result = item.result;
    const score = convertRatingToScore(result);

    // Generic tests don't have ratings
    if (item.ratingType === 'generic') {
      return '(No Rating)';
    }

    // Handle missing ratings
    if (!result) {
      const isRatingRequired = testSuite 
        ? isTestFieldRequired(testSuite, 'rating')
        : false;
      return isRatingRequired ? '(Missing Rating)' : '(No Rating)';
    }

    // Map numeric score back to rating scale (very_poor .. very_good)
    const ratingScale = score
      ? convertScoreToRating(score)
      : 'no_rating';

    // For CONDITION: use scale labels (Very Poor .. Very Good)
    const conditionLabel = getOverallRatingLabel(ratingScale, 'condition');

    // For COMPLIANCE: use binary labels (Not compliant / Compliant)
    const complianceLabel = getOverallRatingLabel(ratingScale, 'compliance');

    // Format score display with 2 decimals
    const scoreDisplay = `(${(score || 0).toFixed(2)} / 5.00)`;

    // Build label based on dataset-level mix (filtered data has both types → show mixed labels everywhere)
    if (isMixedDataset) {
      // Mixed dataset: "Very Poor / Not Compliant (1.00 / 5.00)" or "Very Good / Compliant (5.00 / 5.00)"
      return `${conditionLabel} / ${complianceLabel} ${scoreDisplay}`;
    }

    if (hasComplianceTests && !hasConditionalTests) {
      // Compliance-only dataset: "Not compliant" / "Compliant"
      return complianceLabel;
    }

    if (hasConditionalTests && !hasComplianceTests) {
      // Condition-only dataset: "Very Poor" .. "Very Good"
      return conditionLabel;
    }

    // Fallback
    return `${conditionLabel} ${scoreDisplay}`;
  };

  // Group items by score first, then suite, then category/subcategory/label for compliance
  testItems.forEach((item) => {
    const suiteTitle = getSuiteTitle(item);
    const testSpecification = item.testSpecification;
    const scoreValue = convertRatingToScore(item.result);
    
    // Use numeric score as key, or special keys for no/missing/N/A ratings
    const scoreKey = getScoreKey(
      scoreValue as number | undefined,
      item,
      testSuites
    );

    // Determine if this is a compliance test
    const isCompliance = testSpecification.type === 'compliance';

    // Initialize score node if it doesn't exist
    if (!scoreMap[scoreKey]) {
      const scoreLabel = getRatingLabel(item);
      scoreMap[scoreKey] = {
        id: scoreKey,
        title: scoreLabel,
        score: scoreValue?.toString() ?? undefined,
        children: [],
      };
    }

    const scoreNode = scoreMap[scoreKey];

    // Find or create suite node under score
    let suiteNode = scoreNode.children?.find(
      (s) => s.id === `${scoreKey}.${suiteTitle}`
    );
    if (!suiteNode) {
      suiteNode = {
        id: `${scoreKey}.${suiteTitle}`,
        title: suiteTitle,
        children: [],
      };
      scoreNode.children?.push(suiteNode);
    }

    if (isCompliance) {
      // For compliance: Score > Suite > Category > Subcategory > Label
      const category = testSpecification.category || '(No Category)';
      const subcategory = testSpecification.subcategory || '(No Subcategory)';
      const label = testSpecification.label || '(No Label)';
      
      let categoryNode = suiteNode.children?.find(
        (c) => c.id === `${scoreKey}.${suiteTitle}.${category}`
      );
      if (!categoryNode) {
        categoryNode = {
          id: `${scoreKey}.${suiteTitle}.${category}`,
          title: category,
          children: [],
        };
        suiteNode.children?.push(categoryNode);
      }

      // A tier-less spec (no sub-category) has its tests nest directly under
      // Category, with no sub-category node at all (subcategory-tier.ts, QA 307.08).
      const parentNode = hasSubcategoryTier(testSpecification.subcategory)
        ? (() => {
            let subcategoryNode = categoryNode.children?.find(
              (sc) => sc.id === `${scoreKey}.${suiteTitle}.${category}.${subcategory}`
            );
            if (!subcategoryNode) {
              subcategoryNode = {
                id: `${scoreKey}.${suiteTitle}.${category}.${subcategory}`,
                title: subcategory,
                children: [],
              };
              categoryNode.children?.push(subcategoryNode);
            }
            return subcategoryNode;
          })()
        : categoryNode;

      const labelId = hasSubcategoryTier(testSpecification.subcategory)
        ? `${scoreKey}.${suiteTitle}.${category}.${subcategory}.${label}`
        : `${scoreKey}.${suiteTitle}.${category}.${label}`;

      // Find or create label node under the parent (subcategory, or category when tier-less)
      let labelNode = parentNode.children?.find((ln) => ln.id === labelId);
      if (!labelNode) {
        labelNode = {
          id: labelId,
          title: label,
          content: [],
        };
        parentNode.children?.push(labelNode);
      }

      // Add content to label node
      const contentItem: ReportItemContent = convertTestToReportItem(item);
      labelNode.content?.push(contentItem);
    } else {
      // For condition/generic: Score > Suite (content directly under suite)
      if (!suiteNode.content) {
        suiteNode.content = [];
      }

      // Add content to suite node
      const contentItem: ReportItemContent = convertTestToReportItem(item);
      suiteNode.content?.push(contentItem);
    }
  });

  // Sort and count
  const result = Object.values(scoreMap)
    .sort((a, b) => {
      // Handle special cases first
      const aIsSpecial = !a.score || a.score === 'no_rating' || a.score === 'missing_rating';
      const bIsSpecial = !b.score || b.score === 'no_rating' || b.score === 'missing_rating';
      
      if (aIsSpecial && bIsSpecial) {
        // Both special: missing_rating < no_rating
        if (a.id === 'missing_rating') return 1;
        if (b.id === 'missing_rating') return -1;
        return 0;
      }
      if (aIsSpecial) return 1; // Special cases go last
      if (bIsSpecial) return -1; // Special cases go last
      
      // Both are numeric scores: sort descending (5 before 4 before 3, etc.)
      const aNum = parseFloat(a.score || '0');
      const bNum = parseFloat(b.score || '0');
      return bNum - aNum;
    })
    .map((score) => {
      if (score.children) {
        // Sort suites alphabetically
        score.children.sort((a, b) => customCompare(a.title, b.title));

        score.children.forEach((suite) => {
          // Sort categories (for compliance tests)
          if (suite.children) {
            suite.children.sort((a, b) => customCompare(a.title, b.title));

            // Sort subcategories
            suite.children.forEach((category) => {
              if (category.children) {
                category.children.sort((a, b) => customCompare(a.title, b.title));
                
                // Sort labels
                category.children.forEach((subcategory) => {
                  if (subcategory.children) {
                    subcategory.children.sort((a, b) => customCompare(a.title, b.title));
                  }
                });
              }
            });
          }
        });
      }

      return score;
    });

  result.forEach((score) => {
    countTreeItems(score);
  });

  return result;
}
